import React, { useState, useEffect, useRef, useContext } from 'react';

import { CatalogContext } from 'src/pages/CatalogPage/CatalogPage';
import { formatCurrency, sanitizeWithDashes } from 'src/lib/catalog';
import { AuctionItemActions } from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/AuctionItemActions';
import { ItemIcons } from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/ItemIcons';
import { Banner, Button, cn, Accordion, AccordionItem, Tag, Typography } from '@riac/design-system';
import { ScrollContext } from 'src/lib/ScrollContext';

import { IAuctionItem } from 'src/pages/CatalogPage/CatalogPage.i';

import { ImgLazyLoader } from '@riac/design-system';

import NRAFFFLogo from 'images/NRAFFF LOGO-2.png';
import { getIsPricelessLot } from 'src/lib/getIsPricelessLot';
import { LoginToUse } from 'src/components/LoginToUse/LoginToUse';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
import { timedAuctionGetEndMessage } from 'src/components/timedAuctionGetEndMessage';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);

interface IAuctionItemComponent {
  item: IAuctionItem;
  showPricesRealized: 0 | 1;
}

export const AuctionItem = (props: IAuctionItemComponent) => {
  const { headerPadding } = React.useContext(ScrollContext);
  const itemRef = useRef<HTMLDivElement>(null);
  const { auctionId, auctionStatusId, compactMode, auctionLive, activeLot, auctionTypeId } = useContext(CatalogContext);

  const saniwebtitle = sanitizeWithDashes(props.item.WebTitle);
  const itemLink = `/detail/${auctionId}/${props.item.LotNumber}/${saniwebtitle}`;

  const [readMore, setReadMore] = useState(false);

  const updateHistoryState = (partialState: Record<string, any>) => {
    window.history.pushState(Object.assign(
      {},
      window.history.state,
      partialState,
    ), window.document.title);
  };

  useEffect(() => {
    if(window?.history?.state?.returnLot == props.item.LotNumber) {
      const targetElemRect = itemRef?.current.getBoundingClientRect();
      window.scrollTo(0, targetElemRect.top - headerPadding + (window.scrollY || window.pageYOffset));
      updateHistoryState({ returnLot: (-1) });
    }
  }, [itemRef.current]);
  
  // DayJS Note: Interpret times in America/Chicago timezone
  const endDate = dayjs.tz(props.item.EndDate, 'America/Chicago');
  const now = dayjs().tz('America/Chicago');
  const [timedAuctionLotActive, setTimedAuctionLotActive] = useState(endDate.isAfter(now));

  useEffect(() => {
    const e = dayjs.tz(props.item.EndDate, 'America/Chicago');
    const n = dayjs().tz('America/Chicago');
    const diff = e.diff(n);
    let timer = null;
    if(e.isAfter(n) &&  diff > 0 && diff < 86400000) {
      setTimedAuctionLotActive(true);
      timer = setTimeout(() => {
        setTimedAuctionLotActive(false);
      }, diff+1000);
    }
    return () => {
      clearTimeout(timer);
      }
    }, [props.item.EndDate]);

  const completedLot = auctionTypeId !== TIMED_AUCTION_TYPE_ID && auctionLive && activeLot > props.item.LotNumber;
  const isTimedAuction = auctionTypeId === TIMED_AUCTION_TYPE_ID;

  return (
    <div
      key={props.item.AuctionItemID}
      id={`LotNumber${props.item.LotNumber}`}
      ref={itemRef}
      className='flex flex-col md:flex-row gap-3 md:gap-4 justify-between border-b-1 border-border-dark py-8'
    >
      <div>
        <Button
          unstyled
          to={itemLink}
          onPress={() => updateHistoryState({ returnLot: props.item.LotNumber })}
          className='block w-52 m-auto md:w-24 lg:w-[143px] relative'
        >
          <ImgLazyLoader>
            <object type='image/jpeg' data={`${window.CDN_PATH}${props.item.AuctionID}/255X159/${props.item.Photo}`} className='w-full overflow-hidden'>
              <img src={`${window.CDN_PATH}notfound_255X159.png`} alt={props.item.Headline[0]}/>
            </object>
          </ImgLazyLoader>
          {props.item.LotItemCount > 1 && (
            <div className='absolute bottom-0 right-0 left-0 flex flex-row items-center justify-center'>
              <Tag label={`Lot Contains ${props.item.LotItemCount} Items`} className='bg-neutral-800/80 rounded-0 text-neutral-100 light'/>
            </div>
          )}
          {(props.item.NRAID && props.item.NRAID !== 'false'
            && props.item.NRAID !== '') && <img className='absolute top-1 right-1 w-1/3' src={NRAFFFLogo} alt='NRAFFF LOGO' /> || ''}
        </Button>
      </div>
      <div className='flex flex-col gap-3 flex-1'>
        <div className='flex-1 flex flex-col md:flex-row items-start justify-stretch gap-8'>
          <div className='flex flex-col gap-5 flex-1 basis-full'>
            <div className='flex flex-col'>
              <div>
                <Button
                  unstyled
                  to={itemLink}
                  onPress={() => updateHistoryState({ returnLot: props.item.LotNumber })}
                  className='block'
                >
                  <Typography color='primary' size='sm' weight='bold'>Lot {props.item.LotNumber}</Typography>
                </Button>
              </div>
              <div>
                <Button
                  unstyled
                  to={itemLink}
                  onPress={() => updateHistoryState({ returnLot: props.item.LotNumber })}
                  className='block'
                >
                  <Typography heading expressive weight='bold' className='block line-clamp-2 h-[64px]'>{props.item.WebTitle}</Typography>
                </Button>
              </div>
              {compactMode !== 'true' && 
                <div>
                  <Button
                    unstyled
                    to={itemLink}
                    onPress={() => updateHistoryState({ returnLot: props.item.LotNumber })}
                    className='mt-0.5 block'
                  >
                    <Typography className='block line-clamp-2' size='sm' color='text-lighter'>{props.item.DetailDesc}</Typography>
                  </Button>
                </div>
              }
            </div>
            <div className='flex flex-col gap-1'>
              <ItemIcons item={props.item} />
              <div className='flex flex-col'>
                {props.item.Guage && (
                  <Typography color='text-light' size='sm'>
                    Caliber / Gauge:{' '}
                    {props.item.Guage}
                  </Typography>
                )}
                {(auctionTypeId === TIMED_AUCTION_TYPE_ID) ? (
                  <Typography color='text-light' size='sm'>
                    {timedAuctionGetEndMessage(now, endDate)}
                  </Typography>
                ) : ''}
              </div>
            </div>
          </div>
          <div className={cn(
            'flex h-full flex-1 justify-stretch items-stretch md:min-w-[252px] xl:min-w-[332px] w-full',
            isTimedAuction && [
              'md:min-w-[260px]',
              compactMode == 'true' && 'xl:min-w-[400px]',
            ],
          )}>
            {props.item.AuctionSessionStatus !== 2
              && props.item.AuctionSessionStatus !== 3
              && auctionStatusId !== 2 ? (
                  props.showPricesRealized && props.item.PriceRealized ? (
                    <div>
                      <Banner size='sm' color='primary' centerIconVertically>
                        <div className='flex flex-row flex-wrap items-center justify-stretch min-w-32 gap-0.5'>
                          <div>Price Realized:</div>
                          <LoginToUse overlayText={'Login to view'} hideChildren alwaysShowOverlay className='items-center justify-center flex'>
                            {/* filters generated by https://codepen.io/sosuke/pen/Pjoqqp */}
                            <img className='h-6' src={props.item.PriceRealized} alt='Price obfuscated' style={{filter:'invert(21%) sepia(95%) saturate(2533%) hue-rotate(222deg) brightness(89%) contrast(95%)'}} />
                          </LoginToUse>
                        </div>
                      </Banner>
                    </div>
                  ) : null
                ) : ( !completedLot
                && <AuctionItemActions item={props.item} compact={compactMode === 'true'} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
